<template>
  <div>
    <h3 class="mt-4 page-header-2">Sold Time</h3>
    <div class="overflow-auto">
      <Table class="mt-2 job-times-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Quote #</th>
            <th>Qty</th>
            <th>Task</th>
            <th class="duration-time-header">Total</th>
            <th class="duration-time-header">Sold Time</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in itemsSold"
            :key="item.id"
            :class="getRowStyle(item)"
          >
            <td>
              <div v-if="item.accepted_at">
                {{ formatTableDate(item.accepted_at) }}
              </div>
            </td>
            <td>
              <div
                v-if="item.quote_id"
                class="link"
                @click="openQuote(item.quote_id)"
              >
                {{ item.quote_id }}
              </div>
            </td>

            <td>
              <div v-if="item.items_in_group">
                <div
                  v-for="group_item in item.items_in_group"
                  :key="group_item.id"
                >
                  {{ group_item.quantity }}
                </div>
              </div>
              <div v-else>
                {{ item.quantity }}
              </div>
            </td>

            <td class="task-cell whitespace-normal">
              <div v-if="item.items_in_group">
                <div
                  v-for="group_item in item.items_in_group"
                  :key="group_item.id"
                >
                  {{ group_item.unit_name }}
                </div>
              </div>
              <div v-else>
                {{ item.unit_name }}
              </div>
            </td>

            <td class="text-right">
              <div v-if="item.items_in_group">
                <div
                  v-for="group_item in item.items_in_group"
                  :key="group_item.id"
                >
                  {{ dollars(group_item.total_cents) }}
                </div>
              </div>
              <div v-else>
                {{ dollars(item.total_cents) }}
              </div>
            </td>

            <td class="text-right">
              <div v-if="item.items_in_group">
                <div
                  v-for="group_item in item.items_in_group"
                  :key="group_item.id"
                >
                  <div
                    :class="{
                      link: getProperty(group_item, 'links.time_override')
                    }"
                    @click="handleSelectLineItem(group_item)"
                  >
                    <minutes-label
                      :minutes="group_item.total_minutes"
                      :not-available="!job.job_times_valid"
                      :default-text="missingDataTooltip"
                      :not-available-text="notAvailableTooltip"
                    />
                  </div>
                </div>
              </div>
              <div v-else>
                <div
                  :class="{ link: getProperty(item, 'links.time_override') }"
                  @click="handleSelectLineItem(item)"
                >
                  <minutes-label
                    :minutes="item.total_minutes"
                    :not-available="!job.job_times_valid"
                    :default-text="missingDataTooltip"
                    :not-available-text="notAvailableTooltip"
                  />
                </div>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td>Totals</td>
            <td></td>
            <td></td>
            <td></td>
            <td class="text-right">
              {{ dollars(financials.total_cents) }}
            </td>
            <td class="text-right">
              <minutes-label
                :minutes="job.sold_minutes"
                :not-available="!job.job_times_valid"
                :default-text="missingDataTooltip"
                :not-available-text="notAvailableTooltip"
              />
            </td>
          </tr>
        </tfoot>
      </Table>
    </div>
    <line-item-sold-time-modal
      v-if="currentLineItem"
      :invoice-id="invoiceId"
      :line-item="currentLineItem"
      @hide-modal="clearCurrentLineItem"
    ></line-item-sold-time-modal>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { getProperty } from "@/lib/helpers.js";
import { updateQueryParam } from "@/lib/url.js";

import MinutesLabel from "@/components/MinutesLabel.vue";
import Table from "@/components/Table.vue";
import CONSTANTS from "@/Constants.js";
import LineItemSoldTimeModal from "@/Invoice/LineItemSoldTimeModal.vue";
const { NO_JOB_TIME_TOOLTIP_TEXT, NOT_AVAILABLE_JOB_TIME_TOOLTIP_TEXT } =
  CONSTANTS;

export default {
  components: {
    Table,
    MinutesLabel,
    LineItemSoldTimeModal
  },
  data() {
    return {
      currentLineItem: null
    };
  },
  computed: {
    ...mapState({
      job: "job",
      invoiceId: ({ job }) => getProperty(job, "invoice.invoice_id"),
      financials: ({ job }) => (job.invoice ? job.invoice.financials : {})
    }),
    itemsSold() {
      const { invoice } = this.job;
      let odd = false;
      const invoiceItems = this.getProperty(invoice, "items_attributes") || [];
      const groupByQuoteId = invoiceItems.reduce((acc, current) => {
        if (current.quote_id in acc) {
          acc[current.quote_id] = [
            ...acc[current.quote_id],
            { ...current, quote_id: "", accepted_at: "", odd }
          ];
        } else {
          odd = !odd;
          acc[current.quote_id] = [{ ...current, odd }];
        }
        return acc;
      }, {});
      return Object.values(groupByQuoteId).flat();
    },
    missingDataTooltip() {
      return NO_JOB_TIME_TOOLTIP_TEXT;
    },
    notAvailableTooltip() {
      return NOT_AVAILABLE_JOB_TIME_TOOLTIP_TEXT;
    }
  },
  methods: {
    getProperty,
    async openQuote(quoteId) {
      await updateQueryParam("quote_id", quoteId);
      EventBus.$emit("set-selected-tab", "Quotes");
    },
    getRowStyle({ odd }) {
      return odd ? "odd-background" : "even-background";
    },
    handleSelectLineItem(lineItem) {
      if (lineItem.links.time_override) {
        this.currentLineItem = lineItem;
      }
    },
    clearCurrentLineItem() {
      this.currentLineItem = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.task-cell {
  max-width: 350px;
  white-space: initial;
}

table.job-times-table > tbody > tr.odd-background {
  background-color: var(--white) !important;
}

table.job-times-table > tbody > tr.even-background {
  background-color: var(--angel-blue) !important;
}
</style>
